import { toast } from 'react-toastify';
import { JAVA_API_STATUS } from '../../utils/axios-interceptor';
import {
    AddCompanyToScanPayload,
    AddCompanyToWatchlistPayload,
    addToWatchlistService,
    fetchOrgInfoService,
    fetchOrgUser,
    getAllTaskTrackerDataService,
    getBureauKycConsentTemplateService,
    getEngagementLetterTemplateService,
    getInvestorConfig,
    getOnePagerDataService,
    getOnePagerTemplateDataService,
    inviteCompanyUserService,
    moveCompanyToNewTierService,
    pollOnePagerStatusService,
    saveOnePagerTemplateForAssesseeService,
    unlockAicaForCompanyService,
    UnlockAICAReportPayload,
} from './service';
import { Handlers } from '../../types/actions/types';
import { FetchInvestorConfigPayload } from './action';

export async function addCompanyToWatchlist(payload: AddCompanyToWatchlistPayload, handlers) {
    try {
        const response = await addToWatchlistService(payload);
        const responseData = response.data?.responseData;
        switch (responseData?.responseCode) {
            case JAVA_API_STATUS.SUCCESS:
                handlers?.onSuccess?.(response?.data);
                return;
            case JAVA_API_STATUS.ERROR:
                handlers?.onError?.();
                return toast.error(responseData?.responseMessage || 'Unable to add. Please try again later.');
        }
    } catch (err) {
        handlers?.onError();
        toast.warn('Unable to add. Please try again later.');
    }
}

export async function moveCompanyToNewTier(payload: AddCompanyToScanPayload, handlers: Handlers = {}) {
    try {
        const response = await moveCompanyToNewTierService(payload);
        const responseData = response.data?.responseData;
        switch (responseData?.responseCode) {
            case JAVA_API_STATUS.SUCCESS:
                handlers?.onSuccess?.();
                return;
            case JAVA_API_STATUS.ERROR:
            default:
                handlers?.onError?.();
                throw new Error(responseData.responseMessage || 'Some error occoured');
        }
    } catch (err) {
        handlers?.onError?.();
        throw err;
    }
}

export async function inviteCompanyUser(payload: AddCompanyToScanPayload, handlers: Handlers) {
    try {
        const response = await inviteCompanyUserService(payload);
        const responseData = response.data?.responseData;
        switch (responseData?.responseCode) {
            case JAVA_API_STATUS.SUCCESS:
                handlers?.onSuccess?.();
                return;
            case JAVA_API_STATUS.ERROR:
                handlers?.onError?.();
                return toast.error(responseData?.responseMessage || 'Some error occoured');
        }
    } catch (err) {
        toast.warn('Unable to add. Please try again later.');
    }
}

export async function unlockOrRefreshReport(payload: UnlockAICAReportPayload, handlers: Handlers) {
    try {
        const response = await unlockAicaForCompanyService(payload);
        const responseData = response.data?.responseData;
        switch (responseData?.responseCode) {
            case JAVA_API_STATUS.SUCCESS:
                handlers?.onSuccess?.();
                return;
            case JAVA_API_STATUS.ERROR:
                handlers?.onError?.();
                return toast.error(responseData?.responseMessage || 'Some error occoured');
        }
    } catch (err) {
        handlers?.onError?.();
        toast.warn('Something went wrong');
    }
}

export async function fetchConfigByConfigName(payload: FetchInvestorConfigPayload) {
    try {
        const response = await getInvestorConfig(payload);
        const responseData = response.data?.responseData;

        switch (responseData?.responseCode) {
            case JAVA_API_STATUS.SUCCESS:
                return response.data?.mainConfig;
            case JAVA_API_STATUS.ERROR:
            default:
                return null;
        }
    } catch (err) {
        console.error('Error in fetching config: ', payload.configName, '\nError: ', err);
    }
}

export async function fetchUserFromOrgId(orgId: string, accountType: string, noToast = false) {
    try {
        const response = await fetchOrgUser({ orgId, accountType });
        const responseData = response.data?.responseData;
        switch (responseData?.responseCode) {
            case JAVA_API_STATUS.SUCCESS:
                return response.data?.userDetails;
            case JAVA_API_STATUS.ERROR:
                if (!noToast) toast.error(responseData?.responseMessage || 'Some error occoured');
            default:
                return null;
        }
    } catch (err) {
        console.error('Error in fetching user for orgId: ', orgId, ' accountType: ', accountType, '\nError: ', err);
    }
}

export async function getAllTaskTrackerData(payload: { orgId: string }, handlers: Handlers) {
    try {
        const response = await getAllTaskTrackerDataService(payload);
        const responseData = response.data?.responseData;
        switch (responseData?.responseCode) {
            case JAVA_API_STATUS.SUCCESS:
                handlers?.onSuccess?.(response.data?.responseBody?.taskTrackerData);
                return;
            case JAVA_API_STATUS.ERROR:
                handlers?.onError?.(responseData?.responseMessage || 'Failed to fetch task tracker data');
                return toast.error(responseData?.responseMessage || 'Some error occoured');
        }
    } catch (err) {
        // Pass a safe error object to onError handler to avoid accessing undefined properties
        handlers?.onError?.(err instanceof Error ? err : new Error('Failed to fetch task tracker data'));
    }
}

//ONE PAGER

export async function getOnePagerData(payload: {
    orgId: string;
    // startDate: string; endDate: string
}) {
    try {
        const response = await getOnePagerDataService(payload);
        const responseData = response?.data?.responseData;
        if (responseData?.responseCode === JAVA_API_STATUS.SUCCESS) {
            return response?.data?.responseBody || {};
        } else {
            throw new Error('Error in fetching one pager data: ' + JSON.stringify(responseData || {}));
        }
    } catch (error) {
        console.error('Error in fetching one pager data', error);
        return {};
    }
}

export async function getOnePagerTemplateData(payload: {
    assesseeOrgId: string;
    investorOrgId: string;
    docType?: 'ENGAGEMENT_LETTER' | 'ONE_PAGER' | 'BUREAU_KYC_CONSENT';
}): Promise<{ onePagerData: any; templateData: any[] }> {
    try {
        const response = await getOnePagerTemplateDataService(payload);
        const responseData = response?.data?.responseData;
        if (responseData?.responseCode === JAVA_API_STATUS.SUCCESS) {
            return {
                onePagerData: response?.data || {},
                templateData: response?.data?.onePagerData?.onePagerData || [],
            };
        } else {
            throw new Error('Error in fetching one pager template: ' + JSON.stringify(responseData || {}));
        }
    } catch (error) {
        console.error('Error in fetching one pager template', error);
        return { onePagerData: {}, templateData: [] };
    }
}

export async function saveOnePagerTemplateData(payload: {
    assesseeOrgId: string;
    investorOrgId: string;
    onePagerData: Object[];
    email: string;
    docType?: 'ENGAGEMENT_LETTER' | 'ONE_PAGER' | 'BUREAU_KYC_CONSENT';
}) {
    try {
        const response = await saveOnePagerTemplateForAssesseeService(payload);
        const responseData = response?.data?.responseData;
        if (responseData?.responseCode === JAVA_API_STATUS.SUCCESS) {
            return response?.data?.responseBody || {};
        } else {
            throw new Error('Error in saving one pager template: ' + JSON.stringify(responseData || {}));
        }
    } catch (error) {
        console.error('Error in saving one pager template', error);
        return {};
    }
}

export async function pollOnePagerStatus(payload) {
    try {
        const response = await pollOnePagerStatusService(payload);
        const responseData = response?.data?.responseData;

        if (responseData?.responseCode === JAVA_API_STATUS.SUCCESS) {
            return response?.data?.data || null;
        } else {
            throw new Error('Error occoured in poll one pager status');
        }
    } catch (err) {
        console.error('Error', err);
        return null;
    }
}

// Engagement Letter
export async function fetchEngagementLetterTemplate(payload: { accountType: string; pnoId: string; orgId: string; primaryEmail: string }) {
    try {
        const response = await getEngagementLetterTemplateService(payload);
        const responseData = response?.data?.responseData;
        if (responseData?.responseCode === JAVA_API_STATUS.SUCCESS) {
            return response?.data?.mainConfig?.additionalDetailsConfig?.engagementLetterTemplate || [];
        } else {
            throw new Error('Error in fetching engagement letter template: ' + JSON.stringify(responseData || {}));
        }
    } catch (error) {
        console.error('Error in fetching engagement letter template', error);
        return [];
    }
}

export async function fetchBureauKycConsentTemplate(payload: {
    accountType: string;
    assesseeOrgId: string;
    investorOrgId: string;
    primaryEmail: string;
}) {
    try {
        const response = await getBureauKycConsentTemplateService(payload);
        const responseData = response?.data?.responseData;
        if (responseData?.responseCode === JAVA_API_STATUS.SUCCESS) {
            return response?.data?.mainConfig?.additionalDetailsConfig?.bureauKycConsentTemplate || [];
        } else {
            throw new Error('Error in fetching bureau kyc consent template: ' + JSON.stringify(responseData || {}));
        }
    } catch (error) {
        console.error('Error in fetching bureau kyc consent template', error);
        return [];
    }
}

export async function fetchOrgInfoById(payload: { orgId: string }) {
    try {
        const response = await fetchOrgInfoService(payload);
        const responseData = response?.data?.responseData;
        if (responseData?.responseCode === JAVA_API_STATUS.SUCCESS) {
            return response?.data?.orgInfo || {};
        } else {
            throw new Error('Error in fetching org info by id: ' + JSON.stringify(responseData || {}));
        }
    } catch (error) {
        console.error('Error in fetching org info by id ', error);
        return {};
    }
}
